import { chain, get, includes } from "lodash"
import moment from "moment"
import { getStartDate as getStartDateFromPeriod, getNextModeledDate, isUnmodeledDate } from "./dates"
import { VIEW_TYPES } from "helpers/base"

const EMPTY_OBJECT = {}

function computeEarliestDate(date) {
  const newDate = moment(date)

  if (newDate.isoWeekday() > 4) {
    return newDate.add(1, "weeks").isoWeekday(1).format("YYYY-MM-DD")
  }

  return newDate.add(1, "days").format("YYYY-MM-DD")
}

/*
 * getWorkingPeriod Makes sure that the selected period is within bounds, else return default period
 *
 * getDatesAdjusted returns true if the dates we selected are different than the dates we display
 *
 * getStartDate and getEndDate make sure that the dates of the query are within the period and the availability
 *
 * getSelectedDate makes sure that the date selected is within the bounds of the query date, if not select the last date
  */

export function getComparisonResourcesAvailability(portfoliosAndBenchmarks, earliestDate, indices) {
  if (!portfoliosAndBenchmarks) {
    return EMPTY_OBJECT
  }

  return chain([
    ...get(portfoliosAndBenchmarks, "model.benchmarks"),
    ...get(portfoliosAndBenchmarks, "portfolios"),
    ...indices,
  ])
    .keyBy("id")
    .mapValues(availableFrom => availableFrom > earliestDate ? availableFrom : earliestDate)
    .value()
}

export function getComparisonSecuritiesAvailability(securitiesAvailability, comparisonSecurities) {
  if (!securitiesAvailability?.length) {
    return EMPTY_OBJECT
  }

  return chain(comparisonSecurities)
    .mapKeys(security => security.id)
    .mapValues(security =>
      securitiesAvailability[security.alias]
        ? securitiesAvailability[security.alias].availability
        : null,
    )
    .value()
}

export function getComparisonLatestDate(comparisonItems, portfolioLatestDate, portfolioEarliestDate, securitiesAvailability) {
  let latestDate = portfolioLatestDate

  comparisonItems.forEach(item => {
    // Portfolio and benchmarks can always be rolled over
    if (includes(["security", "swap"], item.type) && securitiesAvailability[item.id] && (securitiesAvailability[item.id].to <= latestDate && securitiesAvailability[item.id].to >= portfolioEarliestDate)) {
      latestDate = securitiesAvailability[item.id].to
    }
  })

  return latestDate
}

export function getComparisonEarliestDate(pnbAvailability, portfolioEarliestDate, comparisonItems, securitiesAvailability, latestDate, selectedComparisonType, unmodeledDates, swaps, modelEarliestDate, experimentAvailability = {}) {
  const comparisonType = get(selectedComparisonType, "id")
  let computedPortfolioEarliestDate = portfolioEarliestDate
  if (comparisonType === "performance") {
    const nextModeledDate = getNextModeledDate(portfolioEarliestDate, latestDate, unmodeledDates)
    if (nextModeledDate <= latestDate) {
      computedPortfolioEarliestDate = nextModeledDate
    }
  }

  let earliestDate = computedPortfolioEarliestDate
  if (earliestDate < modelEarliestDate) {
    earliestDate = modelEarliestDate
  }

  comparisonItems.forEach(item => {
    let itemEarliestDate

    switch (item.type) {
      case "security":
        itemEarliestDate = securitiesAvailability[item.id] ? securitiesAvailability[item.id].from : null
        break
      case "benchmark":
      case "index":
      case "portfolio":
        itemEarliestDate = get(pnbAvailability, [item.id, "availableFrom"])
        break
      case "experiment":
        itemEarliestDate = get(experimentAvailability, item.id)
        break
      case "swap":
        itemEarliestDate = chain(swaps).find(elem => elem.ticker === item.id).get("availableFrom").value()
        break
    }

    if (itemEarliestDate === latestDate) {
      earliestDate = itemEarliestDate
    } else if (itemEarliestDate > earliestDate && itemEarliestDate < latestDate) {
      if (comparisonType !== "performance") {
        earliestDate = itemEarliestDate
      } else {
        earliestDate = computeEarliestDate(itemEarliestDate)
      }
    }
  })

  if (get(unmodeledDates, earliestDate)) {
    let computeNewDate = true

    while (computeNewDate) {
      earliestDate = computeEarliestDate(earliestDate)
      computeNewDate = !!get(unmodeledDates, earliestDate)
    }
  }

  return earliestDate
}

export function getComparisonStartDate(period, latestDate, earliestDate, baseStartDate, viewType, comparisonType, unmodeledDates) {
  const isPerformance = get(comparisonType, "id") === "performance"
  const isActive = viewType === VIEW_TYPES.ACTIVE

  let startDate
  if (period && period.startDate) {
    startDate = period.startDate
  } else {
    const startDateFromPeriod = getStartDateFromPeriod(latestDate, period)

    if (startDateFromPeriod < earliestDate) {
      startDate = earliestDate
    } else {
      startDate = startDateFromPeriod
    }
  }

  if (isActive && startDate <= baseStartDate) {
    if (isPerformance) {
      return getNextModeledDate(baseStartDate, latestDate, unmodeledDates)
    }
    return baseStartDate
  }

  return startDate
}

export function getComparisonStartDateUniversal(period, latestDate, earliestDate, adjustPerformance, unmodeledDates) {
  let startDate
  if (period && period.startDate) {
    startDate = period.startDate
  } else {
    const startDateFromPeriod = getStartDateFromPeriod(latestDate, period)

    if (startDateFromPeriod < earliestDate) {
      startDate = earliestDate
    } else {
      startDate = startDateFromPeriod
    }
  }

  if (isUnmodeledDate(startDate, unmodeledDates)) {
    return getNextModeledDate(startDate, latestDate, unmodeledDates)
  }

  const hasPreviousDate = moment(startDate).diff(moment(earliestDate), "days") >= 1
  if (adjustPerformance && !hasPreviousDate) {
    return getNextModeledDate(startDate, latestDate, unmodeledDates)
  }

  return startDate
}

export function getComparisonWorkingPeriod(period, latestDate, earliestDate) {
  if (period && period.startDate && (period.startDate < earliestDate || period.startDate > latestDate)) {
    return "1Y"
  }

  if (period && period.endDate && (period.endDate < earliestDate || period.endDate > latestDate)) {
    return "1Y"
  }

  return period
}

export function getComparisonEndDate(period, latestDate) {
  if (period && period.endDate) {
    return period.endDate
  }
  return latestDate
}

export function getComparisonSelectedDate(date, startDate, endDate) {
  if (date) {
    if (date < startDate) {
      return startDate
    }
    if (date >= startDate && date <= endDate) {
      return date
    }
  }
  return endDate
}

export function getComparisonExperimentsAvailability(experiments, earliestDate) {
  if (!experiments) {
    return EMPTY_OBJECT
  }
  return chain(experiments)
    .keyBy("id")
    .mapValues(({ availableFrom }) => availableFrom > earliestDate ? availableFrom : earliestDate)
    .value()
}
